import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useAuth } from "./useAuth";
import { EXTERNAL_SUPABASE_URL, EXTERNAL_SUPABASE_PUBLISHABLE_KEY } from "@/integrations/external-supabase/client";

// Issue #740 (Option B) — the one place the app talks about Kroger
// connection state. Before this, 7 independent copies of get/refresh/
// redirect logic lived across TasksTab.tsx (x5), KrogerPricing.tsx, and
// KrogerCallback.tsx, each reading/writing localStorage["kin_kroger_token"]
// directly. The client no longer stores a Kroger token at all — every call
// site asks kroger-token/index.ts for a valid access token (or connection
// status) on demand, authenticated with her real session, same shape as
// useGoogleCalendar.tsx.

const KROGER_TOKEN_URL = `${EXTERNAL_SUPABASE_URL}/functions/v1/kroger-token`;
const KROGER_CART_URL = `${EXTERNAL_SUPABASE_URL}/functions/v1/kroger-cart`;
const KROGER_RETURN_PATH_KEY = "kin_kroger_return_path";

interface KrogerContextValue {
  connected: boolean;
  checking: boolean;
  /** Kicks off the OAuth round-trip: fetches Kroger's auth URL, remembers
   *  where she was, then a same-window full-page redirect (NOT the
   *  Median-external-window pattern useGoogleCalendar.tsx uses — Kroger's
   *  flow has always been same-window; preserved as-is, not "fixed" to
   *  match Google's). */
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
  /** Returns a ready-to-use access token, refreshing server-side first if
   *  needed. Null means not connected (or a dead/revoked refresh token) —
   *  callers should treat null the same as "never connected": offer
   *  connect(), never silently retry with nothing. */
  getValidAccessToken: () => Promise<string | null>;
  checkStatus: () => Promise<void>;
}

const KrogerContext = createContext<KrogerContextValue | null>(null);

export function KrogerProvider({ children }: { children: ReactNode }) {
  const { user, session } = useAuth();
  const [connected, setConnected] = useState(false);
  const [checking, setChecking] = useState(true);

  // Token read from useAuth(), never supabase.auth.getSession() — see the
  // June 29 lock-starvation note in CLAUDE.md.
  const authHeaders = useCallback((): Record<string, string> | null => {
    const token = session?.access_token;
    if (!token) return null;
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      apikey: EXTERNAL_SUPABASE_PUBLISHABLE_KEY,
    };
  }, [session?.access_token]);

  const checkStatus = useCallback(async () => {
    if (!user) { setConnected(false); setChecking(false); return; }
    const h = authHeaders();
    if (!h) { setConnected(false); setChecking(false); return; }
    try {
      const resp = await fetch(KROGER_TOKEN_URL, {
        method: "POST",
        headers: h,
        body: JSON.stringify({ action: "check_status" }),
      });
      if (resp.ok) {
        const data = await resp.json();
        setConnected(!!data.connected);
      }
    } catch {
      // ignore — leaves `connected` at its last known value rather than
      // guessing on a network blip
    } finally {
      setChecking(false);
    }
  }, [user, authHeaders]);

  useEffect(() => { checkStatus(); }, [checkStatus]);

  const connect = useCallback(async () => {
    try {
      const resp = await fetch(KROGER_CART_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${EXTERNAL_SUPABASE_PUBLISHABLE_KEY}` },
        body: JSON.stringify({ action: "getAuthUrl", redirectUri: `${window.location.origin}/kroger/callback` }),
      });
      const data = await resp.json();
      if (!resp.ok || !data.authUrl) throw new Error("oauth-url-failed");
      try {
        sessionStorage.setItem(KROGER_RETURN_PATH_KEY, window.location.pathname + window.location.search);
      } catch {
        // Private browsing or storage quota — callback falls back to "/".
      }
      window.location.href = data.authUrl;
    } catch (err) {
      console.error("[useKroger] connect failed:", err);
      throw err;
    }
  }, []);

  const disconnect = useCallback(async () => {
    // Throws rather than returning silently in both branches below — the
    // Settings confirm dialog does `await disconnect(); close the dialog`,
    // so a silent no-op here would close the dialog and imply success on
    // a disconnect that was never even attempted.
    if (!user) throw new Error("not-authenticated");
    const h = authHeaders();
    if (!h) throw new Error("not-authenticated");
    const resp = await fetch(KROGER_TOKEN_URL, {
      method: "POST",
      headers: h,
      body: JSON.stringify({ action: "disconnect" }),
    });
    if (!resp.ok) throw new Error("disconnect-failed");
    setConnected(false);
  }, [user, authHeaders]);

  const getValidAccessToken = useCallback(async (): Promise<string | null> => {
    const h = authHeaders();
    if (!h) return null;
    try {
      const resp = await fetch(KROGER_TOKEN_URL, {
        method: "POST",
        headers: h,
        body: JSON.stringify({ action: "getValidToken" }),
      });
      const data = await resp.json();
      if (!resp.ok || !data.connected || !data.access_token) {
        setConnected(false);
        return null;
      }
      setConnected(true);
      return data.access_token as string;
    } catch (err) {
      console.error("[useKroger] getValidAccessToken failed:", err);
      return null;
    }
  }, [authHeaders]);

  const value = useMemo<KrogerContextValue>(
    () => ({ connected, checking, connect, disconnect, getValidAccessToken, checkStatus }),
    [connected, checking, connect, disconnect, getValidAccessToken, checkStatus]
  );

  return <KrogerContext.Provider value={value}>{children}</KrogerContext.Provider>;
}

export function useKroger(): KrogerContextValue {
  const ctx = useContext(KrogerContext);
  if (!ctx) throw new Error("useKroger must be used within <KrogerProvider>");
  return ctx;
}
